<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <div class="breadcrumb-spacer"></div>
    <h1 class="page-title">Practice Areas</h1>
    @if (customPracticeAreasEnabled && canCreatePracticeArea) {
      <div class="page-title-actions">
        <div class="create-practice-area" [@.disabled]="!animationsEnabled">
          @if (!isCreating) {
            <div
              #createTrigger
              class="inline-action"
              role="button"
              tabindex="0"
              @expandCreateControl
              (click)="startCreate()"
              (keydown.enter)="startCreate()"
              (keydown.space)="$event.preventDefault(); startCreate()"
            >
              <mat-icon class="inline-action-icon">add</mat-icon>
              <span>New Practice Area</span>
            </div>
          } @else {
            <div
              class="create-form"
              @expandCreateControl
              [class.has-error]="hasCreateError"
              [class.is-wiggling]="isWiggling"
              (animationend)="onWiggleEnd($event)"
            >
              <input
                #newPracticeAreaInput
                class="create-input"
                type="text"
                [(ngModel)]="newPracticeAreaName"
                [ngModelOptions]="{ standalone: true }"
                [maxlength]="maxNameLength"
                [disabled]="isSavingNewPracticeArea"
                placeholder="Practice area name"
                autocomplete="off"
                aria-label="New practice area name"
                [attr.aria-invalid]="hasCreateError || null"
                [attr.aria-describedby]="
                  hasCreateError ? 'create-practice-area-error' : null
                "
                (input)="onNewPracticeAreaInput()"
                (keydown)="onNewPracticeAreaKeyDown($event)"
              />
              <!--
                Keeps the rejection reason attached to the field for assistive tech. The toast
                announces it once and then dismisses; this is what a screen-reader user gets when
                they come back to the field afterwards. Deliberately not a live region — the
                snackbar already announces, and a second one would read the message twice.
              -->
              @if (createErrorMessage) {
                <span id="create-practice-area-error" class="create-error-text">{{
                  createErrorMessage
                }}</span>
              }
              @if (isSavingNewPracticeArea) {
                <div class="create-actions-spinner">
                  <mat-spinner diameter="18"></mat-spinner>
                </div>
              } @else {
                <button
                  mat-icon-button
                  class="inline-save-btn"
                  type="button"
                  matTooltip="Create practice area"
                  [disabled]="!canSubmitNewPracticeArea"
                  (click)="createPracticeArea()"
                >
                  <mat-icon>check</mat-icon>
                </button>
                <button
                  mat-icon-button
                  class="inline-cancel-btn"
                  type="button"
                  matTooltip="Cancel"
                  (click)="cancelCreate()"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>

  <div class="practice-areas-container">
    <!-- Filters Section -->
    <mat-card class="filters-card">
      <mat-card-content>
        <form class="filters-form">
          <div class="filter-row">
            <!-- Search Field -->
            <mat-form-field appearance="outline" class="search-field">
              <mat-label>Search practice areas</mat-label>
              <input
                matInput
                [(ngModel)]="searchQuery"
                [ngModelOptions]="{ standalone: true }"
                placeholder="Search by code or name..."
                autocomplete="off"
                (input)="onSearchInput()"
              />
              <mat-icon
                matSuffix
                [style.cursor]="searchQuery ? 'pointer' : 'default'"
                (click)="clearSearch()"
                >{{ searchQuery ? "close" : "search" }}</mat-icon
              >
            </mat-form-field>
            <button
              mat-stroked-button
              class="reset-filters-btn"
              type="button"
              (click)="clearSearch()"
              [disabled]="!searchQuery"
            >
              <mat-icon>replay</mat-icon>
              Reset
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Loading practice areas...</span>
      </div>
    }

    <!-- Error State -->
    @if (error && !isLoading) {
      <app-error-state
        [message]="error"
        (retry)="loadPracticeAreas()"
      ></app-error-state>
    }

    <!-- Practice Areas Table -->
    @if (!isLoading && !error) {
      <mat-card class="table-card no-padding">
        <mat-card-content>
          <div class="table-container">
            <table
              mat-table
              [dataSource]="dataSource"
              matSort
              [matSortActive]="sortField"
              [matSortDirection]="sortDirection"
              (matSortChange)="sortPracticeAreas($event)"
              class="practice-areas-mat-table"
            >
              <!-- Code Column -->
              <ng-container matColumnDef="practiceCode">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header="practiceCode"
                  class="id-column"
                >
                  Code
                </th>
                <td mat-cell *matCellDef="let practiceArea" class="id-column">
                  <a
                    class="cell-link"
                    [routerLink]="['/admin/practice-areas', practiceArea.practiceCode]"
                  >
                    {{ practiceArea.practiceCode }}
                  </a>
                </td>
              </ng-container>

              <!-- Description Column -->
              <ng-container matColumnDef="description">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header="description"
                >
                  Name
                </th>
                <td
                  mat-cell
                  *matCellDef="let practiceArea"
                  class="practice-area-description"
                >
                  {{ practiceArea.description }}
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns"
              ></tr>

              <!-- Empty State -->
              <tr class="mat-row" *matNoDataRow>
                <td
                  class="mat-cell no-data-cell"
                  [attr.colspan]="displayedColumns.length"
                >
                  <div class="empty-state">
                    <mat-icon>search_off</mat-icon>
                    <span>No practice areas found matching your search</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <mat-paginator
            [length]="totalCount"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            (page)="onPageChange($event)"
            showFirstLastButtons
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    }
  </div>
</div>
